import { required, regex } from 'react-admin';

/**
 * Strict E.164 phone number validator that matches backend requirements
 * Backend expects exact E.164 format: +[country code][national number]
 * - US/Canada: +1 followed by exactly 10 digits (total 12 characters)
 * - International: + followed by 7-15 digits total (E.164 standard)
 * - No formatting characters allowed (parentheses, dashes, spaces)
 */
export const validatePhoneNumber = [
    required('Phone number is required'),
    (value) => {
        if (!value) return 'Phone number is required';
        
        // Remove all whitespace but keep other characters for strict validation
        const trimmedValue = value.trim();
        
        // Must start with +
        if (!trimmedValue.startsWith('+')) {
            return 'Phone number must be in E.164 format starting with + (e.g., +15551234567)';
        }
        
        // Check for any non-digit characters after the +
        const withoutPlus = trimmedValue.slice(1);
        if (!/^\d+$/.test(withoutPlus)) {
            return 'Phone number must contain only digits after the + symbol (no parentheses, dashes, or spaces)';
        }
        
        // Check total length limits per E.164 standard
        if (trimmedValue.length < 8) { // Minimum: +1234567 (7 digits)
            return 'Phone number is too short. Must be at least 7 digits after the + symbol';
        }
        
        if (trimmedValue.length > 16) { // Maximum: +123456789012345 (15 digits)
            return 'Phone number is too long. Must be no more than 15 digits after the + symbol (E.164 standard)';
        }
        
        // For US/Canada numbers (+1), enforce exactly 10 digits after country code
        if (trimmedValue.startsWith('+1')) {
            if (trimmedValue.length !== 12) { // +1 + 10 digits = 12 characters
                return 'US/Canada phone numbers must be exactly +1 followed by 10 digits (e.g., +15551234567)';
            }
        }
        
        return undefined; // Valid
    }
];

/**
 * Validates that at least one hotline (TX or TN) is selected
 */
export const validateHotlineSelection = (value, allValues) => {
    if (!allValues.applies_to_tx && !allValues.applies_to_tn) {
        return 'At least one hotline (TX or TN) must be selected';
    }
    return undefined;
};

/**
 * Validates notes length (max 1000 characters)
 */
export const validateNotes = (value) => {
    if (value && value.length > 1000) {
        return 'Notes cannot exceed 1000 characters';
    }
    return undefined;
};

/**
 * Complete validation for hotline blocklist creation
 */
export const validateHotlineBlocklistCreate = (values) => {
    const errors = {};
    
    // Phone number validation
    const phoneValidators = validatePhoneNumber;
    for (const validator of phoneValidators) {
        const result = validator(values.phone_number);
        if (result) {
            errors.phone_number = result;
            break;
        }
    }
    
    // Hotline selection validation
    const hotlineError = validateHotlineSelection(values.applies_to_tx, values);
    if (hotlineError) {
        errors.applies_to_tx = hotlineError;
    }
    
    // Notes validation
    const notesError = validateNotes(values.notes);
    if (notesError) {
        errors.notes = notesError;
    }
    
    return errors;
};

/**
 * Validation for hotline blocklist edit (similar to create but more flexible)
 */
export const validateHotlineBlocklistEdit = (values) => {
    const errors = {};
    
    // Phone number validation (only if provided)
    if (values.phone_number) {
        const phoneValidators = validatePhoneNumber;
        for (const validator of phoneValidators) {
            const result = validator(values.phone_number);
            if (result) {
                errors.phone_number = result;
                break;
            }
        }
    }
    
    // Hotline selection validation
    const hotlineError = validateHotlineSelection(values.applies_to_tx, values);
    if (hotlineError) {
        errors.applies_to_tx = hotlineError;
    }
    
    // Notes validation
    const notesError = validateNotes(values.notes);
    if (notesError) {
        errors.notes = notesError;
    }
    
    return errors;
}; 